<script setup>
import $axios from '~/helpers/axios';
import { $getSession, $setSession } from '~/helpers/storage';

const { response, helper, t } = useConstructor();
const emit = defineEmits(['update:modelValue']);

// Props
const props = defineProps({
    source: {
        type: String,
        default: null,
    },

    params: {
        type: Object,
        default: null,
    },

    titlecase: {
        type: Boolean,
        default: false,
    },

    reduced: {
        type: Boolean,
        default: false,
    },

    disabled: {
        type: Boolean,
        default: false,
    },

    required: {
        type: Boolean,
        default: false,
    },

    taggable: {
        type: Boolean,
        default: false,
    },

    multiple: {
        type: Boolean,
        default: false,
    },

    label: {
        type: String,
        default: null,
    },

    noLabel: {
        type: Boolean,
        default: false,
    },

    selectLabel: {
        type: String,
        default: 'name',
    },

    modelValue: {
        type: [Object, String, Array],
        default: null,
    },

    name: {
        type: String,
        required: true,
    },

    placeholder: {
        type: String,
        default: null,
    },

    options: {
        type: Array,
        default: () => [],
    },

    prefill: {
        type: Boolean,
        default: false,
    },

    filterBy: {
        type: Function,

        default(option, label, search) {
            return (label || '').toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) > -1;
        },
    },
});

const computedOptions = ref(props.options);
const searching = ref(false);
const focused = ref(false);

//computed
const sourceWithParams = computed(() => {
    if (props.params && Object.keys(props.params).length > 0) {
        const query = new URLSearchParams(props.params).toString();
        return `${props.source}?${query}`;
    }
    return props.source;
});

const _label = computed(() => {
    if (props.label) return props.label;
    if (props.name) return t(helper.headline(props.name));
    return null;
});

const _placeholder = computed(() => {
    if (props.placeholder) return props.placeholder;
    if (_label.value != null) return t('Please Choose') + ' ' + t(helper.headline(_label.value));
    return t('Please Type To Search . . .');
});

const textView = computed(() => {
    let textView = [];
    if (props.modelValue == null || (props.modelValue instanceof Array && props.modelValue.length == 0)) return ['-'];
    if (props.modelValue instanceof Object && props.modelValue.hasOwnProperty('id')) {
        if (props.modelValue[props.selectLabel]) textView.push(t(props.modelValue[props.selectLabel]));
    } else if (props.modelValue instanceof Array) {
        props.modelValue.forEach((item) => {
            if (item instanceof Object && item.hasOwnProperty('id')) {
                if (item[props.selectLabel]) textView.push(t(item[props.selectLabel]));
            } else if (item) textView.push(t(item));
        });
    } else if (props.modelValue) {
        textView.push(t(props.modelValue));
    }

    return textView;
});

//method
const removeWarning = () => {
    if (response?.errors?.hasOwnProperty(props.name)) delete response.errors[props.name];
    if (response?.errors?.hasOwnProperty(`${props.name}.id`)) delete response.errors[`${props.name}.id`];
};

const updateValue = (value) => {
    removeWarning();
    if (props.reduced) emit('update:modelValue', value.id);
    else emit('update:modelValue', value);
};

const removeValue = (value) => {
    removeWarning();
    if (value instanceof Object && value.hasOwnProperty('id')) {
        const newValue = props.modelValue.filter(function (item) {
            return item.id !== value.id;
        });
        emit('update:modelValue', newValue);
    } else if (value) {
        const newValue = props.modelValue.filter(function (item) {
            return item !== value;
        });
        emit('update:modelValue', newValue);
    }
};

const Search = _.debounce((loading, search) => {
    try {
        const urlObj = new URL(sourceWithParams.value, window.location.origin);
        const params = new URLSearchParams(urlObj.search);
        let paramsObject = Object.fromEntries(params.entries());
        paramsObject['search'] = search;

        if (params.value && typeof params.value === 'object') {
            paramsObject = { ...paramsObject, ...params.value };
        }

        $axios
            .get(props.source, { params: paramsObject, skipLoading: true })
            .then((res) => {
                computedOptions.value = res.data.data;
                searching.value = false;
                $setSession(`select:${sourceWithParams.value}`, computedOptions);
                loading(false);
            })
            .catch((err) => {
                searching.value = false;
                loading(false);
            });
    } catch (err) {
        loading(false);
        throw new Error(err);
    }
}, 350);

const onSearch = (search, loading) => {
    if (search?.length > 0) {
        searching.value = true;
        removeWarning();
        loading(true);
        Search(loading, search);
    }
};

const prefill = async () => {
    if (props.source) {
        const cache = $getSession(`select:${sourceWithParams.value}`);
        if (cache) {
            computedOptions.value = cache;
        } else {
            await $axios
                .get(props.source, { params: props.params, skipLoading: true })
                .then((res) => {
                    if (res.data.success === true) {
                        computedOptions.value = res.data.data;
                        $setSession(`select:${props.sourceWithParams}`, computedOptions.value);
                    }
                })
                .catch((err) => {
                    throw new Error(err);
                });
        }
    }
};

const getPrefill = () => {
    if (!focused.value) prefill();
    focused.value = true;
};

const optionCreated = (value) => {
    // return {}
};

onMounted(() => {
    if (props.prefill) getPrefill();
});

watch(
    () => props.source,
    (newSource, oldSource) => {
        if (newSource !== oldSource && !props.noprefill) {
            prefill();
        }
    },
);

watch(
    () => JSON.stringify(props.params),
    (newParams, oldParams) => {
        if (newParams !== oldParams && !props.noprefill) {
            prefill();
        }
    }
);
// buat watch perubahan dinamis options
watch(
    () => props.options,
    (newOptions) => {
        computedOptions.value = newOptions;
    },
    { immediate: true, deep: true },
);
const select2 = ref(null);
</script>

<template>
    <div>
        <template v-if="_label && !noLabel">
            <div v-if="disabled" class="uk-form-label uk-text-capitalize">
                {{ _label }}
            </div>

            <label v-else class="uk-form-label uk-text-capitalize" :for="name">
                {{ _label }}
                <span v-if="required" class="uk-text-danger">(*)</span>

                <div v-if="!response.hasResponse" uk-spinner="ratio: 0.7" class="uk-float-right" />
            </label>
        </template>

        <div v-if="!disabled" class="uk-form-controls uk-inline uk-width-1-1" @mouseover="getPrefill">
            <v-select v-if="source && !reduced" ref="select2" :class="{ 'uk-form-small': !multiple }" :input-id="name"
                :label="selectLabel" :model-value="modelValue" :options="computedOptions" :placeholder="_placeholder"
                :multiple="multiple" :taggable="taggable" :disabled="!response.hasResponse && !searching"
                :filter-by="filterBy" :filterable="false" @search="onSearch" @option:selected="updateValue"
                @option:deselected="removeValue" @option:created="optionCreated" @update:model-value="updateValue" />

            <v-select v-else-if="source && reduced" ref="select2" :class="{ 'uk-form-small': !multiple }"
                :input-id="name" :label="selectLabel" :model-value="modelValue" :options="computedOptions"
                :placeholder="_placeholder" :reduce="computedOptions => computedOptions.id" :multiple="multiple"
                :taggable="taggable" :disabled="!response.hasResponse && !searching" :filter-by="filterBy"
                :filterable="false" @search="onSearch" @option:selected="updateValue" @option:created="optionCreated"
                @update:model-value="updateValue" />

            <v-select v-else-if="!source" ref="select2" :class="{ 'uk-form-small': !multiple }" :input-id="name"
                :label="selectLabel" :model-value="modelValue" :options="computedOptions" :placeholder="_placeholder"
                :multiple="multiple" :taggable="taggable" :disabled="!response.hasResponse && !searching"
                :filter-by="filterBy" @option:selected="updateValue" @option:deselected="removeValue"
                @option:created="optionCreated" @update:model-value="updateValue" />

            <span v-if="response.errors && (response.errors[`${name}.id`] || response.errors[name])"
                class="uk-form-icon uk-form-icon-flip uk-margin-right">
                <AlertCircleIcon color="red" />
            </span>
        </div>

        <div v-else class="uk-form-controls uk-inline uk-width-1-1">
            <p class="uk-margin-remove uk-text-capitalize uk-text-muted">
                <template v-if="multiple">
                    <span v-for="text in textView" :key="text" class="uk-badge uk-margin-small-right"
                        style="display: inline-block; line-height: 1.5;" uk-badge v-html="text" />
                </template>

                <template v-else-if="textView.length > 0">
                    {{ textView.join(', ') }}
                </template>
            </p>
        </div>

        <div v-if="response.errors && (response.errors[`${name}.id`] || response.errors[name])">
            <div class="uk-link-reset uk-text-danger uk-text-small">
                <template v-if="response.errors && response.errors[`${name}.id`]">
                    {{ response.errors[`${name}.id`].toString() }}
                </template>

                <template v-else>
                    {{ response.errors[name].toString() }}
                </template>
            </div>
        </div>
    </div>
</template>
